import React, {useCallback, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Link from 'next/link'
import get from 'lodash/get'
import noop from 'lodash/noop'

import useComponentSize from '@rehooks/component-size'
import ScrollToBottom from 'react-scroll-to-bottom'

import addMessage from 'modules/chat/utils/addMessage'
import Icon from 'components/Icon'
import Header from '../Header'
import Messages from '../Messages'
import Compose from '../Compose'
import ShareDetails from '../ShareDetails'

import styles from './styles.module.scss'

const ChatBoxView = ({
  room,
  user,
  pin,
  messages,
  encryptionKeys,
  keyManager,
  isLoading,
  isLoadingMoreMessages,
  hasMoreMessages,
  hasError,
  showDetailsOnLoad,
  onClickLoadMoreMessages,
}) => {
  const messagesContainerRef = useRef(null)
  const messagesContainerSize = useComponentSize(messagesContainerRef)

  const [isFull, setIsFull] = useState(false)
  const roomId = get(room, 'uid')
  const createdAt = get(room, 'createdAt')
  const description = get(room, 'description')
  const creatorName = get(room, 'creator.name')
  const userId = get(user, 'uid')

  const handleOnAddMessage = useCallback(
    async (msg, clearMessage) => {
      try {
        clearMessage()
        await addMessage({keyManager, roomId, msg, user})
      } catch (err) {
        // @TODO: handle errors
      }
    },
    [user, keyManager, roomId]
  )

  const toggleIsFull = useCallback(() => setIsFull(isFull => !isFull), [])

  return (
    <div
      className={classNames('box', styles.container, {
        [styles.containerIsFull]: isFull,
      })}>
      <div className={styles.headerContainer}>
        <Header
          createdAt={createdAt}
          description={description}
          creatorName={creatorName}
          toolbar={
            <>
              <ShareDetails
                room={room}
                pin={pin}
                defaultIsActive={showDetailsOnLoad}
              />
              <button
                type="button"
                className="button is-white is-hidden-mobile"
                data-tooltip={isFull ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                onClick={toggleIsFull}>
                <span className="icon">
                  <Icon icon={isFull ? 'compress' : 'expand'} size="lg" />
                </span>
              </button>
            </>
          }
          additionalMessage={
            <h6
              className={classNames(
                'is-size-7 has-text-grey-lighter',
                styles.poweredBy
              )}>
              Powered by{' '}
              <Link href="/">
                <a className="has-text-grey-light">reden.me</a>
              </Link>
            </h6>
          }
        />
      </div>
      <div
        ref={messagesContainerRef}
        className={classNames(
          'has-background-light',
          styles.messagesContainer
        )}>
        <ScrollToBottom followButtonClassName={styles.scrollToBottomBtn}>
          <Messages
            roomId={roomId}
            userId={userId}
            messages={messages}
            hasMore={hasMoreMessages}
            onClickLoadMore={onClickLoadMoreMessages}
            encryptionKeys={encryptionKeys}
            keyManager={keyManager}
            isLoading={isLoading}
            isLoadingMore={isLoadingMoreMessages}
            height={messagesContainerSize.height}
          />
        </ScrollToBottom>
      </div>
      <div className={styles.composeContainer}>
        <Compose
          roomId={roomId}
          user={user}
          disabled={isLoading}
          onAddMessage={handleOnAddMessage}
        />
      </div>
    </div>
  )
}

ChatBoxView.defaultProps = {
  hasMoreMessages: false,
  onClickLoadMoreMessages: noop,
  isLoadingMoreMessages: false,
  isLoading: false,
  hasError: false,
}

ChatBoxView.propTypes = {
  encryptionKeys: PropTypes.shape({
    public: PropTypes.string,
    private: PropTypes.string,
  }),
  hasError: PropTypes.bool,
  hasMoreMessages: PropTypes.bool,
  isLoading: PropTypes.bool,
  isLoadingMoreMessages: PropTypes.bool,
  keyManager: PropTypes.object,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string.isRequired,
      createdAt: PropTypes.number,
      message: PropTypes.string,
      from: PropTypes.shape({
        uid: PropTypes.string,
        name: PropTypes.string,
      }),
    })
  ),
  onClickLoadMoreMessages: PropTypes.func,
  pin: PropTypes.string,
  room: PropTypes.shape({
    createdAt: PropTypes.number,
    description: PropTypes.string,
    creator: PropTypes.shape({
      uid: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  showDetailsOnLoad: PropTypes.bool,
  user: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
  }),
}

export default ChatBoxView
